import React, { useState } from "react";
import { X, Mail, Bell, AlertCircle } from "lucide-react";

interface RejectKYCModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notificationMethods: string[]) => void;
  userName: string;
  loading?: boolean;
}

export const RejectKYCModal: React.FC<RejectKYCModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userName,
  loading = false,
}) => {
  const [reason, setReason] = useState("");
  const [notificationMethods, setNotificationMethods] = useState<string[]>(["EMAIL"]);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError("Veuillez spécifier une raison pour le rejet");
      return;
    }

    if (notificationMethods.length === 0) {
      setError("Veuillez sélectionner au moins une méthode de notification");
      return;
    }

    onConfirm(reason, notificationMethods);
  };

  const toggleNotificationMethod = (method: string) => {
    setNotificationMethods((prev) => {
      if (prev.includes(method)) {
        return prev.filter((m) => m !== method);
      } else {
        return [...prev, method];
      }
    });
    setError("");
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Rejeter le KYC</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Vous êtes sur le point de rejeter le KYC de <strong>{userName}</strong>
          </p>
        </div>

        {/* Raison du rejet */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Raison du rejet <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={handleReasonChange}
            disabled={loading}
            placeholder="Ex: Documents illisibles, photo floue, informations manquantes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
            rows={4}
          />
        </div>

        {/* Méthodes de notification */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Méthode de notification <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notificationMethods.includes("EMAIL")}
                onChange={() => toggleNotificationMethod("EMAIL")}
                disabled={loading}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <Mail className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-700">Email</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notificationMethods.includes("PUSH")}
                onChange={() => toggleNotificationMethod("PUSH")}
                disabled={loading}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-700">Push Notification</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notificationMethods.includes("BOTH")}
                onChange={() => {
                  if (notificationMethods.includes("BOTH")) {
                    setNotificationMethods([]);
                  } else {
                    setNotificationMethods(["BOTH"]);
                  }
                  setError("");
                }}
                disabled={loading}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-gray-600" />
                <Bell className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm text-gray-700">Les deux</span>
            </label>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Traitement...
              </>
            ) : (
              "Confirmer le rejet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
